import styled from '@emotion/styled/macro'
import { themeColor, themeFont } from 'mixins'
import { Blob } from 'model'
import React from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useUploadBlobMutation } from './FileUpload.generated'

const getColor = (props: any) => {
  if (props.isDragAccept) {
    return themeColor('greenLight')
  }
  if (props.isDragReject) {
    return themeColor('red')
  }
  if (props.isDragActive) {
    return themeColor('blueLight')
  }
  return themeColor('neutralLight')
}

export const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
  ${themeFont('mediumPlus')};
`
export interface FileUploadProps {
  parentId: string
  parentType: string
  onUploadCompleted: (files: Partial<Blob>[]) => void
  onFileUploaded?: (file: Partial<Blob>) => {}
  disabled?: boolean
  singleFile?: boolean
  text?: string
  noDragEventsBubbling?: boolean
  accept?: string | string[]
}

export const FileUpload: React.FunctionComponent<FileUploadProps> = ({
  onUploadCompleted,
  onFileUploaded,
  parentId,
  parentType,
  disabled,
  singleFile,
  text,
  accept,
}) => {
  const { t } = useTranslation()
  const [uploadBlob] = useUploadBlobMutation()

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept,
    disabled,
    multiple: !singleFile,
    noDragEventsBubbling: true,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      const files = await Promise.all(
        acceptedFiles.map(async (f) => {
          // Create a new Blob from the file, because there is an issue with ASCII in filenames and multipart uploads
          const blob = f.slice(0, f.size, f.type)
          // Set the name property on the blob
          blob.name = encodeURIComponent(f.name)

          const result = await uploadBlob({
            variables: {
              parentId,
              parentType,
              file: blob, // Use the blob instead of the file
            },
          })
          if (onFileUploaded) {
            onFileUploaded(result.data!.uploadBlob as Partial<Blob>)
          }
          return result.data!.uploadBlob
        })
      )
      onUploadCompleted(files as Partial<Blob>[])
    },
  })

  return (
    <div className="container">
      <Container
        {...getRootProps({
          isDragActive,
          isDragAccept,
          isDragReject,
        })}
      >
        <input {...getInputProps()} />
        {text ? <p>{text}</p> : <p>{t('field.fileUpload.defaultText')}</p>}
      </Container>
    </div>
  )
}
